import React, { useState } from "react";
import {
  Box, Button, Card, Chip, Collapse, Divider, InputAdornment, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import type { Options } from "../types";

export type FilterState = {
  search: string;
  include: "active" | "future" | "previous" | "all";
  study_mode: string[];
  school_grade_id: number[];
  subject_id: number[];
  staff_id: number[];
  day: string[];
  location_id: number[];
  assignment_status: string;
  class_status: string;
  start_date: string;
  end_date: string;
};

export const defaultFilter: FilterState = {
  search: "",
  include: "active",
  study_mode: [],
  school_grade_id: [],
  subject_id: [],
  staff_id: [],
  day: [],
  location_id: [],
  assignment_status: "",
  class_status: "",
  start_date: "",
  end_date: "",
};

const humanMode = (m: string) =>
  m === "Online" ? "Online"
  : m === "Offline" || m === "in_person" ? "In Person"
  : m === "Hybrid" ? "Hybrid" : m;

type Props = {
  value: FilterState;
  onChange: (v: FilterState) => void;
  onClear: () => void;
  loading: boolean;
  options: Options;
};

export default function FilterPanel({ value, onChange, onClear, loading, options }: Props) {
  const [open, setOpen] = useState(false);

  const set = <K extends keyof FilterState>(k: K, v: FilterState[K]) => onChange({ ...value, [k]: v });

  const chips: { key: string; label: string; onDelete: () => void }[] = [];
  if (value.search) chips.push({ key: "s", label: `Search: “${value.search}”`, onDelete: () => set("search", "") });
  value.study_mode.forEach((m) => chips.push({
    key: `m${m}`, label: `Mode: ${humanMode(m)}`,
    onDelete: () => set("study_mode", value.study_mode.filter((x) => x !== m)),
  }));
  value.school_grade_id.forEach((id) => chips.push({
    key: `g${id}`, label: `Grade: ${options.grades.find((g) => g.id === id)?.label || id}`,
    onDelete: () => set("school_grade_id", value.school_grade_id.filter((x) => x !== id)),
  }));
  value.subject_id.forEach((id) => chips.push({
    key: `sub${id}`, label: `Subject: ${options.subjects.find((s) => s.id === id)?.label || id}`,
    onDelete: () => set("subject_id", value.subject_id.filter((x) => x !== id)),
  }));
  value.staff_id.forEach((id) => chips.push({
    key: `st${id}`, label: `Staff: ${options.staff.find((s) => s.id === id)?.label || id}`,
    onDelete: () => set("staff_id", value.staff_id.filter((x) => x !== id)),
  }));
  value.day.forEach((d) => chips.push({
    key: `d${d}`, label: `Day: ${d}`,
    onDelete: () => set("day", value.day.filter((x) => x !== d)),
  }));
  value.location_id.forEach((id) => chips.push({
    key: `l${id}`, label: `Location: ${options.locations.find((l) => l.id === id)?.label || id}`,
    onDelete: () => set("location_id", value.location_id.filter((x) => x !== id)),
  }));
  if (value.assignment_status) chips.push({
    key: "as", label: `Assignment: ${value.assignment_status}`,
    onDelete: () => set("assignment_status", ""),
  });
  if (value.class_status) chips.push({
    key: "cs", label: `Class Status: ${value.class_status}`,
    onDelete: () => set("class_status", ""),
  });
  if (value.start_date) chips.push({
    key: "sd", label: `From: ${value.start_date}`,
    onDelete: () => set("start_date", ""),
  });
  if (value.end_date) chips.push({
    key: "ed", label: `To: ${value.end_date}`,
    onDelete: () => set("end_date", ""),
  });

  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
        <TextField
          size="small" fullWidth
          placeholder="Search student, staff, class, or reference ID"
          value={value.search}
          onChange={(e) => set("search", e.target.value)}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchOutlinedIcon fontSize="small" /></InputAdornment>),
          }}
          sx={{ maxWidth: { md: 420 } }}
          disabled={loading}
        />
        <TextField select size="small" label="Assignment Scope" value={value.include}
          onChange={(e) => set("include", e.target.value as any)} sx={{ minWidth: 200 }}>
          <MenuItem value="active">Active Assignments</MenuItem>
          <MenuItem value="future">Future Assignments</MenuItem>
          <MenuItem value="previous">Previous Assignments</MenuItem>
          <MenuItem value="all">All Assignments</MenuItem>
        </TextField>
        <TextField select size="small" label="Study Mode"
          SelectProps={{ multiple: true, renderValue: (v: any) => (v as string[]).map(humanMode).join(", ") }}
          value={value.study_mode}
          onChange={(e) => set("study_mode", e.target.value as any as string[])}
          sx={{ minWidth: 180 }}>
          {options.study_modes.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
        </TextField>
        <Button variant="outlined" size="small" startIcon={<TuneOutlinedIcon />}
          onClick={() => setOpen((x) => !x)}>
          {open ? "Hide filters" : "More filters"}
        </Button>
        <Button size="small" onClick={onClear}>Clear</Button>
      </Stack>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField select size="small" label="Student Grade"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => options.grades.find((g) => g.id === id)?.label || id).join(", ") }}
            value={value.school_grade_id}
            onChange={(e) => set("school_grade_id", e.target.value as any as number[])}
            sx={{ minWidth: 180 }}>
            {options.grades.map((g) => <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Subject"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => options.subjects.find((s) => s.id === id)?.label || id).join(", ") }}
            value={value.subject_id}
            onChange={(e) => set("subject_id", e.target.value as any as number[])}
            sx={{ minWidth: 200 }}>
            {options.subjects.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Staff"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => options.staff.find((s) => s.id === id)?.label || id).join(", ") }}
            value={value.staff_id}
            onChange={(e) => set("staff_id", e.target.value as any as number[])}
            sx={{ minWidth: 200 }}>
            {options.staff.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Day"
            SelectProps={{ multiple: true, renderValue: (v: any) => (v as string[]).join(", ") }}
            value={value.day}
            onChange={(e) => set("day", e.target.value as any as string[])}
            sx={{ minWidth: 180 }}>
            {options.days.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Location"
            SelectProps={{ multiple: true, renderValue: (v: any) =>
              (v as number[]).map((id) => options.locations.find((l) => l.id === id)?.label || id).join(", ") }}
            value={value.location_id}
            onChange={(e) => set("location_id", e.target.value as any as number[])}
            sx={{ minWidth: 200 }}>
            {options.locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}
          </TextField>

          <TextField select size="small" label="Assignment Status"
            value={value.assignment_status}
            onChange={(e) => set("assignment_status", e.target.value)}
            sx={{ minWidth: 180 }}>
            <MenuItem value="">Any</MenuItem>
            {options.assignment_statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>

          <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }}
            value={value.start_date} onChange={(e) => set("start_date", e.target.value)}
            sx={{ minWidth: 160 }} />

          <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }}
            value={value.end_date} onChange={(e) => set("end_date", e.target.value)}
            sx={{ minWidth: 160 }} />
        </Stack>
      </Collapse>

      {chips.length > 0 && (
        <Box mt={1.5}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Active filters:</Typography>
          {chips.map((c) => (
            <Chip key={c.key} size="small" label={c.label} onDelete={c.onDelete}
              sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </Box>
      )}
    </Card>
  );
}
